import {
    RevenueByDay,
    RevenueByMonth,
    RevenueReportByRentalType,
    RevenueRentalType,
    YoYRevenue,
} from 'types/TransactionReport';
import { Period, RevenueType, rentalTypeOptions, resolveSelectedRentalTypes } from 'components/RevenueWidgets/helpers';
import { convertCentsToDisplayPrice, currencyWithoutCents } from 'utils/currency';
import { months } from 'utils/constants';
import { getLabelFromOptions } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import {
    getDayMonthYearFromString,
    getMonthDateFromString,
    getMonthNameFromString,
    getMonthYearFromString,
} from 'utils/date';

export type TableData = { headers: string[]; rows: string[][] };

// ============ Revenue Page Table Data ============

type RevenueTableParams = {
    timeframe: 'monthly' | 'daily';
    revenueType: RevenueType;
    momYearCompare?: 'on' | 'off';
    dailyPeriod?: Period;
    momCurrentYearReport: RevenueByMonth | null;
    momPreviousYearReport?: RevenueByMonth | null;
    dodReport?: RevenueByDay | null;
};

export function getRevenueTableData(params: RevenueTableParams): TableData | null {
    const {
        timeframe,
        revenueType,
        momYearCompare,
        dailyPeriod,
        momCurrentYearReport,
        momPreviousYearReport,
        dodReport,
    } = params;

    const isGrossRevenue = revenueType === RevenueType.GROSS_REVENUE;

    // Monthly timeframe
    if (timeframe === 'monthly' && momCurrentYearReport) {
        // Monthly with YoY comparison - years as columns
        if (momYearCompare === 'on' && momPreviousYearReport) {
            const allReports = [...momPreviousYearReport, ...momCurrentYearReport];
            const years = [...new Set(allReports.map((r) => getDayMonthYearFromString(r.date).year.toString()))].sort();
            const headers = ['Months', ...years];

            // Group data by month name
            const monthData: Record<string, Record<string, string>> = {};
            for (const report of allReports) {
                const monthName = getMonthNameFromString(report.date);
                const year = getDayMonthYearFromString(report.date).year;
                const revenue = isGrossRevenue ? report.formatted_gross_revenue : report.formatted_net_revenue;
                if (!monthData[monthName]) monthData[monthName] = {};
                monthData[monthName][year] = revenue;
            }

            // Sort months by calendar order
            const sortedMonths = Object.keys(monthData).sort((a, b) => months.indexOf(a) - months.indexOf(b));
            const rows = sortedMonths.map((month) => [month, ...years.map((year) => monthData[month][year] || '')]);

            return { headers, rows };
        }

        // Monthly without YoY - simple two column table
        const headers = ['Months', 'Revenue'];
        const rows = momCurrentYearReport.map((item) => [
            getMonthYearFromString(item.date),
            isGrossRevenue ? item.formatted_gross_revenue : item.formatted_net_revenue,
        ]);
        return { headers, rows };
    }

    // Daily timeframe
    if (timeframe === 'daily' && dodReport) {
        const dayRows = dodReport.map((item) => ({
            day: getMonthDateFromString(item.date),
            revenue: currencyWithoutCents.format((isGrossRevenue ? item.gross_revenue : item.net_revenue) / 100),
        }));

        // Two column layout for 30 days
        if (dailyPeriod === Period.THIRTY) {
            const headers = ['Days', 'Revenue', 'Days', 'Revenue'];
            const midpoint = Math.ceil(dayRows.length / 2);
            const leftColumn = dayRows.slice(0, midpoint);
            const rightColumn = dayRows.slice(midpoint);

            const rows = leftColumn.map((left, index) => {
                const right = rightColumn[index];
                return [left.day, left.revenue, right?.day || '', right?.revenue || ''];
            });

            return { headers, rows };
        }

        // Single column layout for 7 or 14 days
        const headers = ['Days', 'Revenue'];
        const rows = dayRows.map((item) => [item.day, item.revenue]);
        return { headers, rows };
    }

    return null;
}

// ============ By Rental Type Page Table Data ============

type ByRentalTypeTableParams = {
    revenueByRentalTypeReport: RevenueReportByRentalType | null;
    rentalTypeFilter: RevenueRentalType | '';
    // Whether this spot shares ticket revenue with the owner; without it the "Violations"
    // column is omitted entirely rather than exported as a row of $0.00s.
    hasTicketRevenue: boolean;
};

export function getByRentalTypeTableData(params: ByRentalTypeTableParams): TableData {
    const { revenueByRentalTypeReport, rentalTypeFilter, hasTicketRevenue } = params;

    if (!revenueByRentalTypeReport) return { headers: [], rows: [] };

    const selectedRentalTypes = resolveSelectedRentalTypes(rentalTypeFilter, hasTicketRevenue);

    const rows: string[][] = [];
    for (const dateData of revenueByRentalTypeReport) {
        const row = [getMonthYearFromString(dateData.date)];
        for (const type of selectedRentalTypes) {
            // `ticket` is absent from the report for spots that don't share ticket
            // revenue with the owner, so it won't be present on every dateData entry.
            const revenue = dateData[type as RevenueRentalType];
            row.push(revenue ? revenue.formatted_gross_revenue : convertCentsToDisplayPrice(0));
        }
        rows.push(row);
    }

    const headers = ['Months', ...selectedRentalTypes.map((h) => getLabelFromOptions(rentalTypeOptions, h))];
    return { headers, rows };
}

// ============ Historical Page Table Data ============

type HistoricalTableParams = {
    yoyRevenueReport: YoYRevenue | null;
    yoyRevenueType: RevenueType;
};

export function getHistoricalTableData(params: HistoricalTableParams): TableData {
    const { yoyRevenueReport, yoyRevenueType } = params;

    if (!yoyRevenueReport || yoyRevenueReport.reports.length === 0) {
        return { headers: [], rows: [] };
    }

    const headers = ['Top Month', 'Revenue', 'Year-over-year Change'];
    const rows: string[][] = [];
    const isGrossRevenue = yoyRevenueType === RevenueType.GROSS_REVENUE;

    let previousRevenue: number | null = null;

    // Note: we reverse to process from oldest to newest for YoY calculation
    const sortedReports = [...yoyRevenueReport.reports].reverse();

    for (const report of sortedReports) {
        const revenue = isGrossRevenue ? report.gross_revenue : report.net_revenue;
        const formattedRevenue = currencyWithoutCents.format(revenue / 100);

        let yoyChange = '0%';
        if (previousRevenue !== null && previousRevenue > 0) {
            const change = revenue - previousRevenue;
            const percentChange = Math.round((change / previousRevenue) * 100);
            const formattedChange = currencyWithoutCents.format(Math.abs(change) / 100);
            const sign = change >= 0 ? '+' : '-';
            yoyChange = `${formattedChange} (${sign}${Math.abs(percentChange)}%)`;
        }

        rows.push([getMonthYearFromString(report.date), formattedRevenue, yoyChange]);
        previousRevenue = revenue;
    }

    return { headers, rows };
}
